/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of GameTimeFootage Inc.
 #*
 #* Copyright NextPro 2019. All Rights Reserved.
 #*
 #*===================================================================
 */

import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AssetsService, FormHelpersService } from 'app/shared/services';
import { Constants } from 'app/shared/models/constants.model';
import { environment } from 'environments/environment';
import { TranscoderService } from 'app/shared/services/transcoder.service';
import { UtilsService } from 'app/shared/services/utils.service';
import * as fromSharedStore from 'app/shared/store';
import { Store } from '@ngrx/store';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { HttpEventType } from '@angular/common/http';

@Component({
  selector: 'app-highlight-reel-upload-modal',
  templateUrl: './highlight-reel-upload.component.html',
  styleUrls: ['./highlight-reel-upload.component.scss'],
})
export class HighlightReelUploadModalComponent implements OnInit {
  @Input() userEvents: any = [];

  @Output() closeModal: EventEmitter<any> = new EventEmitter();

  @ViewChild('videoUpload') videoUpload: ElementRef;

  selectedTeams: any[];
  newHrForm: FormGroup;
  uploading = false;
  fileLoaded = false;
  fileDrag = false;
  file: File;
  invalidFile: boolean;

  readonly MODAL_TYPES = Constants.HIGHLIGHT_REEL_PRO_PLAY_TYPES;

  uploadProgress = 0;

  private ongoingUploadReq: any;
  private componentDestroyed: Subject<any> = new Subject();
  private currentUser: any;

  constructor(
    public formHelpersService: FormHelpersService,
    private assetsService: AssetsService,
    private transcoderService: TranscoderService,
    private sharedStore: Store<fromSharedStore.SharedState>,
    private utilsService: UtilsService
  ) {}

  ngOnInit() {
    this.initForm();
    this.sharedStore
      .select(fromSharedStore.getUserData)
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((currentUser) => {
        this.currentUser = currentUser.userData;
      });
  }

  initForm() {
    this.newHrForm = new FormGroup({
      title: new FormControl('', Validators.required),
      videoUrl: new FormControl('', Validators.required),
      originalVideoUrl: new FormControl(''),
      newFilename: new FormControl(''),
    });
  }

  close() {
    this.closeModal.emit(null);
  }

  save() {
    if (this.newHrForm.invalid) {
      this.formHelpersService.markFieldsAsDirty(this.newHrForm);
      return;
    }
    const uploadedHr = {
      ...this.newHrForm.value,
      file: this.file,
    };

    let bucket;
    let videoPath;
    this.uploading = true;
    this.uploadProgress = 0;

    this.ongoingUploadReq = this.assetsService
      .uploadFileToBucket(
        `${this.currentUser.username}/${uploadedHr.newFilename}`,
        uploadedHr.file,
        environment.userUploadBucketName
      )
      .pipe(
        tap((response) => {
          if (response.loaded && response.total) {
            this.uploadProgress = Math.ceil((100 * response.loaded) / response.total);
          }
        }),
        filter((response) => response.ok && response.type === HttpEventType.Response),
        switchMap((resp) => {
          bucket = resp.body.bucket;
          videoPath = resp.body.name;

          const pathSegments = uploadedHr.videoUrl.split('/');
          return this.assetsService.addVideoAsset({
            contentType: 'video/mp4',
            published: false,
            metadata: { isUserUpload: true },
            type: Constants.ASSET_TYPE.ATHLETE_CUSTOM_VIDEO,
            name: uploadedHr.title,
            originalName: pathSegments[pathSegments.length - 1],
            url: uploadedHr.videoUrl,
          });
        }),
        switchMap((asset) => {
          uploadedHr.asset = asset;
          return this.assetsService.setCustomAsset(asset.id, this.currentUser.id);
        }),
        switchMap(() => {
          const uploadPath = uploadedHr.asset.url.replace(`${Constants.GS_ASSET_HOSTNAME}/`, '');
          return this.transcoderService.pushHRToQueue({
            path: uploadPath,
            assetId: uploadedHr.asset.id,
            // set higher priority
            priority: Constants.TRANSCODER_PRIORITIES.MEDIUM,
            bucket,
            downloadPath: videoPath,
            publishWhenDone: true,
          });
        })
      )
      .subscribe(() => {
        this.closeModal.emit(uploadedHr);
      });
  }

  get videoUrl() {
    return this.newHrForm.get('videoUrl').value;
  }

  ngOnDestroy() {
    if (this.ongoingUploadReq) {
      this.ongoingUploadReq.unsubscribe();
    }
    this.componentDestroyed.next();
    this.componentDestroyed.complete();
  }

  resetFile() {
    this.file = null;
    this.videoUpload.nativeElement.value = '';
    this.initForm();
  }

  onDrop($event) {
    $event.preventDefault();
    this.fileDrag = false;
    this.fileChanged({ target: $event.dataTransfer });
  }

  onDragOver(event) {
    event.stopPropagation();
    event.preventDefault();
    this.fileDrag = true;
  }

  onDragLeave(event) {
    event.stopPropagation();
    event.preventDefault();
    this.fileDrag = false;
  }

  fileChanged(event) {
    this.file = null;
    if (!event.target.files) {
      return;
    }

    const file: File = event.target.files[0];
    let extension = file.name.slice(file.name.lastIndexOf('.') + 1);
    if (!extension) {
      return;
    }
    extension = extension.toLowerCase();
    const hasValidFileType = extension === 'mp4' || extension === 'mpeg' || extension === 'mov';
    const hasValidFileSize = file.size >= 0.1 * Math.pow(1024, 2) && file.size <= 0.5 * Math.pow(1024, 3);
    this.invalidFile = !hasValidFileSize || !hasValidFileType;

    if (!this.invalidFile) {
      this.file = file;
      const randomSuffix = this.utilsService.generateRandomString(4);
      const newFilename = `${this.file.name.replace(`.${extension}`, '')}_${randomSuffix}.${extension}`;
      this.newHrForm.get('newFilename').setValue(newFilename);
      this.newHrForm
        .get('originalVideoUrl')
        .setValue(
          Constants.GS_ASSET_HOSTNAME +
            '/video_assets/' +
            `_user_uploaded_hr/${this.currentUser.username}/${newFilename}`
        );
      this.newHrForm
        .get('videoUrl')
        .setValue(
          Constants.GS_ASSET_HOSTNAME +
            '/video_assets/' +
            `_user_uploaded_hr/${this.currentUser.username}/${newFilename.replace(`.${extension}`, '.mp4')}`
        );
    }
  }
}
